import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSafeSelectHandler } from '@/hooks/useResizeObserverFix';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SafeSelect as Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/SafeSelect';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
// import { Calendar } from '@/components/ui/calendar'; // Temporairement désactivé pour éviter ResizeObserver error

// Icons
import { UserCheck, UserX, Clock, Plus, Calendar as CalendarIcon, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AttendanceManagement = ({ user }) => {
  const [attendance, setAttendance] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('record');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date()); // Réactivé pour éviter l'erreur
  const [selectedNiveau, setSelectedNiveau] = useState('');
  const [statistics, setStatistics] = useState({});
  
  const [attendanceForm, setAttendanceForm] = useState({
    student_id: '',
    class_id: '',
    date: new Date().toISOString().split('T')[0],
    status: '',
    is_justified: false,
    reason: '',
    justification_document: ''
  });

  const attendanceStatuses = [
    { value: 'PRESENT', label: 'Présent', icon: CheckCircle, color: 'text-green-600' },
    { value: 'ABSENT', label: 'Absent', icon: XCircle, color: 'text-red-600' },
    { value: 'LATE', label: 'Retard', icon: Clock, color: 'text-orange-600' },
    { value: 'EXCUSED', label: 'Excusé', icon: AlertTriangle, color: 'text-blue-600' }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (user.role === 'STUDENT' || user.role === 'PARENT') {
      fetchMyAttendance();
    } else {
      fetchAttendanceStatistics();
    }
  }, [user.role]);

  // Handler sécurisé pour les changements de niveau utilisant requestAnimationFrame
  const handleNiveauChange = useSafeSelectHandler(
    useCallback((niveauId) => {
      setSelectedNiveau(niveauId);
      if (niveauId) {
        fetchClassAttendance(niveauId, selectedDate);
      }
    }, [selectedDate])
  );

  const fetchInitialData = async () => {
    try {
      const promises = [];
      
      if (user.role !== 'STUDENT' && user.role !== 'PARENT') {
        promises.push(
          axios.get(`${API}/classes`),
          axios.get(`${API}/students`)
        );
      }
      
      const results = await Promise.all(promises);
      
      if (results.length > 0) {
        setNiveaux(results[0].data);
        setStudents(results[1].data);
        if (results[0].data.length > 0) {
          setSelectedNiveau(results[0].data[0].id);
        }
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyAttendance = async () => {
    try {
      const response = await axios.get(`${API}/attendance/my-attendance`);
      setAttendance(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des absences');
      console.error('Error fetching my attendance:', error);
    }
  };

  const fetchClassAttendance = async (classId, date = null) => {
    try {
      const params = date ? { date: date.toISOString().split('T')[0] } : {};
      const response = await axios.get(`${API}/attendance/class/${classId}`, { params });
      setAttendance(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des présences');
      console.error('Error fetching class attendance:', error);
    }
  };

  const fetchAttendanceStatistics = async () => {
    try {
      const response = await axios.get(`${API}/attendance/statistics`);
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching attendance statistics:', error);
    }
  };

  const handleRecordAttendance = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/attendance`, {
        ...attendanceForm,
        date: new Date(attendanceForm.date).toISOString(),
        is_justified: attendanceForm.is_justified
      });
      
      toast.success('Présence enregistrée avec succès');
      resetForm();
      
      // Rafraîchir les données après enregistrement
      if (selectedNiveau) {
        fetchClassAttendance(selectedNiveau, selectedDate);
      }
      
      // Actualiser les statistiques automatiquement
      fetchAttendanceStatistics();
      
      setDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
      console.error('Error recording attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setAttendanceForm({
      student_id: '',
      class_id: selectedNiveau || '',
      date: new Date().toISOString().split('T')[0],
      status: '',
      is_justified: false,
      reason: '',
      justification_document: ''
    });
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : 'Élève inconnu';
  };

  const getNiveauName = (niveauId) => {
    const niveauObj = niveaux.find(c => c.id === niveauId);
    return niveauObj ? niveauObj.name : 'Niveau inconnu';
  };

  const getStatusInfo = (status) => {
    return attendanceStatuses.find(s => s.value === status) || attendanceStatuses[0];
  };

  const canManageAttendance = ['TEACHER', 'LIFE_COUNSELOR', 'ADMIN', 'PRINCIPAL'].includes(user.role);

  if (loading && attendance.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in" data-testid="attendance-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <UserCheck className="w-6 h-6 mr-2 text-blue-600" />
            {user.role === 'STUDENT' || user.role === 'PARENT' ? 'Mes Absences' : 'Gestion des Présences'}
          </h1>
          <p className="text-gray-600 mt-1">
            {user.role === 'STUDENT' || user.role === 'PARENT' 
              ? 'Consultez votre historique de présences et d\'absences' 
              : 'Enregistrez et gérez les présences des élèves'
            }
          </p>
        </div>
        
        {canManageAttendance && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="record-attendance-btn"
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Enregistrer Présence
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Enregistrer une présence</DialogTitle>
                <DialogDescription>
                  Marquez la présence ou l'absence d'un élève
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleRecordAttendance} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="class_id">Niveau</Label>
                  <Select 
                    value={attendanceForm.class_id} 
                    onValueChange={(value) => setAttendanceForm({ ...attendanceForm, class_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez un niveau" />
                    </SelectTrigger>
                    <SelectContent>
                      {niveaux.map((niveauObj) => (
                        <SelectItem key={niveauObj.id} value={niveauObj.id}>
                          {niveauObj.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="student_id">Élève</Label>
                  <Select 
                    value={attendanceForm.student_id} 
                    onValueChange={(value) => setAttendanceForm({ ...attendanceForm, student_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez un élève" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map((student) => (
                        <SelectItem key={student.id} value={student.id}>
                          {student.first_name} {student.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={attendanceForm.date}
                    onChange={(e) => setAttendanceForm({ ...attendanceForm, date: e.target.value })}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="status">Statut</Label>
                  <Select 
                    value={attendanceForm.status} 
                    onValueChange={(value) => setAttendanceForm({ ...attendanceForm, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Statut de présence" />
                    </SelectTrigger>
                    <SelectContent>
                      {attendanceStatuses.map((status) => {
                        const IconComponent = status.icon;
                        return (
                          <SelectItem key={status.value} value={status.value}>
                            <div className="flex items-center">
                              <IconComponent className={`w-4 h-4 mr-2 ${status.color}`} />
                              {status.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                
                {(attendanceForm.status === 'ABSENT' || attendanceForm.status === 'LATE') && (
                  <>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="is_justified"
                        checked={attendanceForm.is_justified}
                        onChange={(e) => setAttendanceForm({ ...attendanceForm, is_justified: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor="is_justified">Absence justifiée</Label>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="reason">Motif (optionnel)</Label>
                      <Textarea
                        id="reason"
                        value={attendanceForm.reason}
                        onChange={(e) => setAttendanceForm({ ...attendanceForm, reason: e.target.value })}
                        placeholder="Motif de l'absence ou du retard..."
                        rows={2}
                      />
                    </div>
                  </>
                )}
                
                <div className="flex justify-end space-x-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setDialogOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Statistics for admin roles */}
      {canManageAttendance && Object.keys(statistics).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Enregistrements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_records || 0}</div>
              <p className="text-xs text-gray-600 mt-1">Présences enregistrées</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Absences</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{statistics.absent_records || 0}</div>
              <p className="text-xs text-gray-600 mt-1">Élèves absents</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Retards</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{statistics.late_records || 0}</div>
              <p className="text-xs text-gray-600 mt-1">Élèves en retard</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Taux d'Absence</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{statistics.absence_rate || 0}%</div>
              <p className="text-xs text-gray-600 mt-1">Pourcentage d'absences</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="record">
            {canManageAttendance ? 'Enregistrements' : 'Mon Historique'}
          </TabsTrigger>
          <TabsTrigger value="calendar">Calendrier</TabsTrigger>
        </TabsList>
        
        {/* Records Tab */}
        <TabsContent value="record" className="space-y-4">
          {/* Class and Date selector for admins */}
          {canManageAttendance && (
            <Card className="card">
              <CardContent className="pt-6">
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <Label>Niveau:</Label>
                    <Select value={selectedNiveau} onValueChange={handleNiveauChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir un niveau" />
                      </SelectTrigger>
                      <SelectContent>
                        {niveaux.map((niveauObj) => (
                          <SelectItem key={niveauObj.id} value={niveauObj.id}>
                            {niveauObj.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex-1">
                    <Label>Date:</Label>
                    <Input
                      type="date"
                      value={selectedDate.toISOString().split('T')[0]}
                      onChange={(e) => {
                        const newDate = new Date(e.target.value);
                        setSelectedDate(newDate);
                        if (selectedNiveau) {
                          fetchClassAttendance(selectedNiveau, newDate);
                        }
                      }}
                    />
                  </div>
                  
                  <Button 
                    onClick={() => selectedNiveau && fetchClassAttendance(selectedNiveau, selectedDate)}
                    className="btn-secondary mt-6"
                  >
                    Charger
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Attendance List */}
          {attendance.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {attendance.map((record) => {
                const statusInfo = getStatusInfo(record.status);
                const StatusIcon = statusInfo.icon;
                
                return (
                  <Card key={record.id} className="card">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <StatusIcon className={`w-5 h-5 ${statusInfo.color}`} />
                            <h3 className="font-semibold">{statusInfo.label}</h3>
                          </div>
                          
                          <p className="text-sm text-gray-600 mt-1">
                            {user.role === 'STUDENT' || user.role === 'PARENT' 
                              ? `Niveau: ${getNiveauName(record.class_id)}`
                              : `Élève: ${getStudentName(record.student_id)}`
                            }
                          </p>
                          
                          <p className="text-sm text-gray-600">
                            Date: {new Date(record.date).toLocaleDateString('fr-FR')}
                          </p>
                          
                          {record.reason && (
                            <p className="text-sm text-gray-700 mt-2 italic">
                              Motif: {record.reason}
                            </p>
                          )}
                          
                          <div className="flex items-center space-x-2 mt-2">
                            {record.is_justified && (
                              <Badge className="badge-success">Justifié</Badge>
                            )}
                            <span className="text-xs text-gray-500">
                              {new Date(record.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <UserCheck className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {user.role === 'STUDENT' || user.role === 'PARENT' 
                  ? 'Aucun enregistrement de présence' 
                  : 'Aucune présence enregistrée'
                }
              </h3>
              <p className="text-gray-600">
                {user.role === 'STUDENT' || user.role === 'PARENT'
                  ? 'Votre historique de présences apparaîtra ici.'
                  : 'Sélectionnez un niveau et une date pour voir les présences.'
                }
              </p>
            </div>
          )}
        </TabsContent>
        
        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-4">
          <Card className="card">
            <CardHeader>
              <CardTitle className="flex items-center">
                <CalendarIcon className="w-5 h-5 mr-2" />
                Calendrier des Présences
              </CardTitle>
              <CardDescription>
                Vue calendaire des absences et présences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                <div className="w-80 h-80 rounded-md border border-gray-200 flex items-center justify-center bg-gray-50">
                  <div className="text-center">
                    <CalendarIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Calendrier des Présences</h3>
                    <p className="text-gray-600">
                      Vue calendaire en cours de développement
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <p className="text-center text-gray-600">
                  Fonctionnalité calendrier en cours de développement
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AttendanceManagement;